/**
 * GameStage — the one framed surface every game renders inside.
 *
 * Fixes three audit findings at once:
 *  - C2 "no contained stage": a responsive, max-width branded frame instead of
 *    a tiny widget floating in a void.
 *  - C1 "dark brand vanishes": a derived `stageGradient` that always contrasts
 *    the play surface, plus neutral translucent chrome that reads on any brand.
 *  - extras: a corner sound toggle + the win Celebration mount, shared by all.
 *
 * Games pass their play surface as `children` and their action button as
 * `footer`. The frame owns background, sizing, reduced-motion, sound and
 * confetti so individual games stay focused on their mechanic.
 */
import type { ReactNode } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { stageGradient, withAlpha, SURFACE_LINE } from './colors';
import type { SoundController } from './useGameSound';
import { Celebration, type CelebrationController } from './Celebration';

interface GameStageProps {
  primary: string;
  accent: string;
  children: ReactNode;
  footer?: ReactNode;
  sound?: SoundController;
  celebration?: CelebrationController;
  /** Aspect ratio for the play surface (e.g. 1 or '4 / 5'). Optional. */
  aspect?: number | string;
  maxWidth?: number;
  className?: string;
  contentClassName?: string;
}

export function GameStage({
  primary,
  accent,
  children,
  footer,
  sound,
  celebration,
  aspect,
  maxWidth = 460,
  className,
  contentClassName,
}: GameStageProps) {
  const t = useT();
  return (
    <div
      data-game-motion
      className={cn('relative flex w-full flex-col items-center gap-5', className)}
    >
      <div
        className="relative w-full overflow-hidden rounded-[28px] border"
        style={{
          maxWidth,
          background: stageGradient(primary, accent),
          borderColor: SURFACE_LINE,
          boxShadow: `0 20px 50px -20px ${withAlpha('#000000', 0.7)}, inset 0 1px 0 ${withAlpha(
            '#ffffff',
            0.12,
          )}`,
        }}
      >
        {sound && (
          <button
            type="button"
            onClick={sound.toggle}
            aria-label={t(sound.enabled ? 'publicSurvey.games.soundOn' : 'publicSurvey.games.soundOff')}
            aria-pressed={sound.enabled}
            className="absolute right-2.5 top-2.5 z-20 grid h-9 w-9 place-items-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            {sound.enabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </button>
        )}

        <div
          className={cn(
            'flex items-center justify-center p-5 sm:p-6',
            contentClassName,
          )}
          style={aspect ? { aspectRatio: String(aspect) } : undefined}
        >
          {children}
        </div>

        {celebration && (
          <Celebration controller={celebration} primary={primary} accent={accent} />
        )}
      </div>

      {footer && (
        <div className="flex flex-col items-center gap-3">{footer}</div>
      )}
    </div>
  );
}
